import React from 'react';
import {
  Button, EmptyState, ErrorState, FormField, JalaliDatePicker, Pager, Select, TextInput,
} from '@parsina/framework';
import { Activity, SearchX, X } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import {
  LEDGER_PAGE_SIZE, LEDGER_STATUSES, OWNER_TYPES, ledgerFilter, ledgerIsFiltered, pageCountOf,
} from '../../lib/aiPricingView';
import { INVOCATIONS_QUERY, LEDGER_OPTIONS_QUERY } from './queries';
import LedgerTable from './LedgerTable';
import InvocationDrawer from './InvocationDrawer';
import { CsvExportButton, TableSkeleton, useServiceLabel } from './shared';

/**
 * View «دفترکل» — Archetype B, forensic, and the panel's first SERVER-PAGED
 * list (design §12.7): every filter and the page number travel to the BFF as
 * `AiInvocationFilter` + `page`, because a 90-day ledger is not an array the
 * browser should hold.
 *
 * The date range is composed from two `JalaliDatePicker`s rather than the
 * kit's `DateRangeFilter`, whose copy is hardcoded Persian and whose popover
 * pins `direction: rtl` and a physical `left` — unusable in a bilingual panel.
 */
export default function LedgerView({ window, view, go }) {
  const { t, formatNumber } = useTranslation();
  const label = useServiceLabel();

  const [options, setOptions] = React.useState({ services: [], models: [] });
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    gql(LEDGER_OPTIONS_QUERY, { window })
      .then((d) => {
        if (!alive) return;
        setOptions({
          services: d?.aiServices?.services || [],
          models: (d?.aiModels || []).map((m) => m.modelId),
        });
      })
      // The option lists are a convenience; the ledger itself reports failure.
      .catch(() => {});
    return () => { alive = false; };
  }, [window]);

  const filter = React.useMemo(() => ledgerFilter({ ...view, w: view.w }), [view]);
  const page = Math.max(1, Number(view.page) || 1);

  const load = React.useCallback(({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true); else setLoading(true);
    setError(null);
    gql(INVOCATIONS_QUERY, { filter, page, pageSize: LEDGER_PAGE_SIZE })
      .then((d) => setData(d?.aiInvocations || { invocations: [], total: 0, page, pageSize: LEDGER_PAGE_SIZE }))
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [filter, page, t]);

  const first = React.useRef(true);
  React.useEffect(() => {
    load({ quiet: !first.current });
    first.current = false;
  }, [load]);

  const labelOf = React.useCallback((key) => {
    const row = options.services.find((s) => s.serviceKey === key);
    return row ? label(row) : key;
  }, [options.services, label]);

  const rows = data?.invocations || [];
  const isFiltered = ledgerIsFiltered(view);
  const clear = () => go({ service: '', model: '', owner: '', ownerId: '', status: '', charged: '', dfrom: '', dto: '', page: 1 });
  const setFilter = (patch) => go({ ...patch, page: 1 });

  const csvColumns = [
    { key: 'requestId', header: t('aiPricing.ledger.drawer.requestId'), value: (r) => r.requestId },
    { key: 'startedAt', header: t('aiPricing.ledger.columns.time'), value: (r) => r.startedAt },
    { key: 'serviceKey', header: t('aiPricing.ledger.columns.service'), value: (r) => r.serviceKey },
    { key: 'ownerType', header: t('aiPricing.ledger.filters.ownerType'), value: (r) => r.ownerType },
    { key: 'ownerId', header: t('aiPricing.ledger.filters.ownerId'), value: (r) => r.ownerId },
    { key: 'calls', header: t('aiPricing.ledger.columns.calls'), value: (r) => r.calls },
    { key: 'inputTokens', header: t('aiPricing.services.drawer.columns.avgIn'), value: (r) => r.inputTokens },
    { key: 'outputTokens', header: t('aiPricing.services.drawer.columns.avgOut'), value: (r) => r.outputTokens },
    { key: 'costUsd', header: t('aiPricing.ledger.columns.cost'), value: (r) => r.costUsd },
    { key: 'chargedParseh', header: t('aiPricing.ledger.columns.charged'), value: (r) => r.chargedParseh },
    { key: 'chargeReason', header: t('aiPricing.ledger.drawer.chargeReason'), value: (r) => r.chargeReason },
    { key: 'priceSource', header: t('aiPricing.ledger.columns.source'), value: (r) => r.priceSource },
    { key: 'status', header: t('aiPricing.ledger.columns.status'), value: (r) => r.status },
    { key: 'walletTransactionId', header: t('aiPricing.ledger.drawer.wallet'), value: (r) => r.walletTransactionId },
  ];

  const all = { value: '', label: t('aiPricing.ledger.filters.all') };

  return (
    <div data-testid="ai-ledger">
      <div
        className="mb-2 grid grid-cols-2 gap-3 rounded-lg border border-border bg-card p-3 md:grid-cols-4 xl:grid-cols-8"
        role="group"
        aria-label={t('aiPricing.ledger.filters.label')}
        data-testid="ai-ledger-filters"
      >
        <Select
          label={t('aiPricing.ledger.filters.service')}
          value={view.service || ''}
          onChange={(e) => setFilter({ service: e.target?.value ?? e })}
          options={[all, ...options.services.map((s) => ({ value: s.serviceKey, label: label(s) }))]}
          placeholder={t('aiPricing.ledger.filters.all')}
          data-testid="ai-ledger-filter-service"
        />
        <Select
          label={t('aiPricing.ledger.filters.model')}
          value={view.model || ''}
          onChange={(e) => setFilter({ model: e.target?.value ?? e })}
          options={[all, ...options.models.map((m) => ({ value: m, label: m }))]}
          placeholder={t('aiPricing.ledger.filters.all')}
          // Model ids are identifiers: the whole control is an LTR island.
          dir="ltr"
          data-testid="ai-ledger-filter-model"
        />
        <Select
          label={t('aiPricing.ledger.filters.ownerType')}
          value={view.owner || ''}
          onChange={(e) => setFilter({ owner: e.target?.value ?? e })}
          options={[all, ...OWNER_TYPES.map((o) => ({ value: o, label: t(`aiPricing.overview.owner.${o}`) }))]}
          placeholder={t('aiPricing.ledger.filters.all')}
          data-testid="ai-ledger-filter-owner"
        />
        <TextInput
          label={t('aiPricing.ledger.filters.ownerId')}
          value={view.ownerId || ''}
          onChange={(e) => setFilter({ ownerId: e.target?.value ?? e })}
          dir="ltr"
          inputStyle={{ fontFamily: 'monospace' }}
          data-testid="ai-ledger-filter-owner-id"
        />
        <Select
          label={t('aiPricing.ledger.filters.status')}
          value={view.status || ''}
          onChange={(e) => setFilter({ status: e.target?.value ?? e })}
          options={[all, ...LEDGER_STATUSES.map((s) => ({ value: s, label: t(`aiPricing.ledger.status.${s}`) }))]}
          placeholder={t('aiPricing.ledger.filters.all')}
          data-testid="ai-ledger-filter-status"
        />
        <Select
          label={t('aiPricing.ledger.filters.charged')}
          value={view.charged || ''}
          onChange={(e) => setFilter({ charged: e.target?.value ?? e })}
          options={[all, { value: 'yes', label: t('aiPricing.ledger.charged.yes') }, { value: 'no', label: t('aiPricing.ledger.charged.no') }]}
          placeholder={t('aiPricing.ledger.filters.all')}
          data-testid="ai-ledger-filter-charged"
        />
        <FormField label={t('aiPricing.ledger.filters.from')}>
          <div data-testid="ai-ledger-filter-from">
            <JalaliDatePicker value={view.dfrom || ''} onChange={(v) => setFilter({ dfrom: v || '' })} placeholder={t('aiPricing.ledger.filters.datePlaceholder')} disableFuture />
          </div>
        </FormField>
        <FormField label={t('aiPricing.ledger.filters.to')}>
          <div data-testid="ai-ledger-filter-to">
            <JalaliDatePicker value={view.dto || ''} onChange={(v) => setFilter({ dto: v || '' })} placeholder={t('aiPricing.ledger.filters.datePlaceholder')} disableFuture />
          </div>
        </FormField>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <p className="text-xs text-muted-foreground" aria-live="polite" data-testid="ai-ledger-count">
          {data ? t('aiPricing.ledger.count', { count: data.total || 0 }) : null}
        </p>
        {isFiltered ? (
          <Button variant="ghost" size="sm" onClick={clear} data-testid="ai-ledger-clear">
            <X size={14} aria-hidden />
            {t('common.actions.clearFilters')}
          </Button>
        ) : null}
        <div className="ms-auto">
          <CsvExportButton columns={csvColumns} rows={rows} data-testid="ai-ledger-csv" />
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card transition-opacity" style={{ opacity: refreshing ? 0.45 : 1 }} aria-busy={refreshing || undefined}>
        {loading ? (
          <TableSkeleton cols={9} label={t('aiPricing.ledger.loading')} data-testid="ai-ledger-loading" />
        ) : error ? (
          <ErrorState
            title={t('aiPricing.ledger.error')}
            description={error}
            onRetry={load}
            retryLabel={t('common.actions.retry')}
            data-testid="ai-ledger-error"
          />
        ) : (
          <LedgerTable
            rows={rows}
            labelOf={labelOf}
            onOpen={(r) => go({ inv: r.requestId })}
            empty={isFiltered ? (
              <EmptyState
                icon={<SearchX size={44} strokeWidth={1.5} aria-hidden />}
                title={t('aiPricing.ledger.emptyFiltered.title')}
                description={t('aiPricing.ledger.emptyFiltered.desc')}
                action={<Button variant="outline" onClick={clear}>{t('common.actions.clearFilters')}</Button>}
                data-testid="ai-ledger-empty"
              />
            ) : (
              <EmptyState
                icon={<Activity size={44} strokeWidth={1.5} aria-hidden />}
                title={t('aiPricing.ledger.empty.title')}
                description={t('aiPricing.ledger.empty.desc')}
                data-testid="ai-ledger-empty"
              />
            )}
          />
        )}
      </div>

      {!loading && !error ? (
        <div data-testid="ai-ledger-pager">
          <Pager
            page={page}
            pageCount={pageCountOf(data?.total)}
            pageSize={LEDGER_PAGE_SIZE}
            onChange={(next) => go({ page: next })}
            prevLabel={t('common.pager.prev')}
            nextLabel={t('common.pager.next')}
            pageLabel={(p, total) => t('common.pager.label', { page: p, total })}
            formatNumber={formatNumber}
          />
        </div>
      ) : null}

      {view.inv ? (
        <InvocationDrawer requestId={view.inv} labelOf={labelOf} onClose={() => go({ inv: '' })} />
      ) : null}
    </div>
  );
}
